import { faAngleRight } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { exhaustive } from "exhaustive"
import { filter, sortBy } from "lodash"
import { FC, useEffect, useState } from "react"
import { useClient } from "../../Client/ClientAndUserProvider"
import { ConsumerCatalogConfiguration, PreferredVATRenderPolicy } from "../../Client/consumerCatalogConfigurationTypes"
import { ConsumerFeatures } from "../../Client/ConsumerCatalogContext"
import { Loader } from "../../Loader/Loader"
import { API } from "../../network/API"
import { PermissionAreaLocation, usePermissions } from "../../PermissionContext"
import {
	DynamicConsumerGroupConsumerAssignmentModal,
	EditFixedGroup,
} from "../DynamicConsumerGroupConsumerAssignmentModal/DynamicConsumerGroupConsumerAssignmentModal"
import style from "./CustomerPortalConsumerGroups.module.css"

export type ConsumerGroup = {
	name: string
	consumerCatalogConfiguration: ConsumerCatalogConfiguration
	features?: ConsumerFeatures
}

export type DynamicGroup = {
	id: string
	group: ConsumerGroup
}

export type GetDynamicGroupsResponse = {
	dynamicGroups: DynamicGroup[]
	fixedGroups: Record<GetDynamicGroupsFixedType, ConsumerGroup>
}

export enum GetDynamicGroupsFixedType {
	Companies = "Companies",
	PrivatePersons = "PrivatePersons",
}

type CustomerPortalConsumerGroupsProps = {}

export type GetDynamicConsumerGroupConnectionsResponse = {
	connections: GetDynamicConsumerGroupConnection[]
}

export type GetDynamicConsumerGroupConnection = {
	id: string
	dynamicGroupId: string
	consumerRefs: string[]
}

export type GetConsumerGroupResponse = {
	dynamicGroup?: DynamicGroup
	fixedGroup: ConsumerGroup
}

type EditGroup = EditDynamicGroup | EditFixed

type EditFixed = {
	type: "Fixed"
	group: EditFixedGroup
}

type EditDynamicGroup = {
	type: "Dynamic"
	dynamicGroup: DynamicGroup
}

export const CustomerPortalConsumerGroups: FC<CustomerPortalConsumerGroupsProps> = (
	props: CustomerPortalConsumerGroupsProps,
) => {
	const client = useClient()
	const permissions = usePermissions()

	const [dynamicGroups, setDynamicGroups] = useState<DynamicGroup[]>([])
	const [fixedGroups, setFixedGroups] = useState<Record<GetDynamicGroupsFixedType, ConsumerGroup> | null>(null)
	const [editGroup, setEditGroup] = useState<EditGroup | null>(null)
	const [dynamicGroupConnections, setDynamicGroupConnections] = useState<GetDynamicConsumerGroupConnection[]>([])

	const [dataLoading, setDataLoading] = useState(true)

	useEffect(() => {
		fetchAvailableDynamicGroups()
	}, [])

	async function fetchAvailableDynamicGroups() {
		setDataLoading(true)
		setDynamicGroups([])

		if (!permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Groups_Read)) {
			setDataLoading(false)
			return
		}

		const resGroups = await API.getWithRetries<GetDynamicGroupsResponse>(
			`/customer-portal/dynamic-consumer-groups-v1/${client.identifier}`,
			true,
		)
			.then((res) => {
				return res
			})
			.catch(() => {
				setDynamicGroups([])
				setFixedGroups(null)
				return null
			})

		const resConnections = await fetchCurrentDynamicGroupConnections()

		if (resGroups) {
			setDynamicGroups(
				sortBy(resGroups.dynamicGroups, (dynamicGroup) => {
					return dynamicGroup.group.name.normalize("NFC").toLocaleLowerCase()
				}),
			)
			setFixedGroups(resGroups.fixedGroups)
		}
		setDynamicGroupConnections(resConnections)
		setDataLoading(false)
	}

	async function fetchCurrentDynamicGroupConnections(): Promise<GetDynamicConsumerGroupConnection[]> {
		if (!permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Groups_Assign)) {
			return Promise.resolve([])
		}

		return API.getWithRetries<GetDynamicConsumerGroupConnectionsResponse>(
			`/customer-portal/dynamic-consumer-groups-v1/${client.identifier}/group-connections`,
			true,
		)
			.then((res) => {
				return res.connections
			})
			.catch(() => {
				return []
			})
	}

	function assignConsumersModal() {
		if (!permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Groups_Assign) || !editGroup || !fixedGroups) {
			return null
		}

		let dynamicGroup: DynamicGroup | null = null
		let fixedGroup: EditFixedGroup | null = null
		let otherConnections: GetDynamicConsumerGroupConnection[] = []
		let dynamicGroupConnection: GetDynamicConsumerGroupConnection | null = null

		exhaustive(editGroup, "type", {
			Dynamic: (it) => {
				dynamicGroup = it.dynamicGroup
				dynamicGroupConnection =
					dynamicGroupConnections.find((x) => x.dynamicGroupId === it.dynamicGroup.id) || null
			},
			Fixed: (it) => {
				fixedGroup = it.group
			},
		})

		otherConnections = filter(dynamicGroupConnections, (connection) => {
			return connection.id !== (dynamicGroupConnection?.id || "--")
		})

		if ((!dynamicGroup && !fixedGroup) || (!!dynamicGroup && !!fixedGroup)) {
			return
		}

		return (
			<DynamicConsumerGroupConsumerAssignmentModal
				dynamicGroup={dynamicGroup}
				fixedGroup={fixedGroup}
				allGroups={dynamicGroups}
				dynamicGroupConnection={dynamicGroupConnection}
				otherConnections={otherConnections}
				allConnections={dynamicGroupConnections}
				fixedGroups={fixedGroups}
				onClose={() => {
					setEditGroup(null)
				}}
				onSave={async () => {
					await fetchAvailableDynamicGroups()
				}}
			/>
		)
	}

	function dynamicGroupsSection() {
		const allowedToEdit = permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Groups_Assign)

		return (
			<div className={style.contentWrapper}>
				<div className={style.header}>Kundgrupper</div>
				<div className={style.groupsWrapper}>
					{dynamicGroups.map((dynamicGroup) => {
						return (
							<div
								key={dynamicGroup.id}
								className={style.consumerGroup}
								onClick={() => {
									if (allowedToEdit) {
										setEditGroup({
											type: "Dynamic",
											dynamicGroup: dynamicGroup,
										})
									}
								}}>
								<div className={style.consumerGroupContent}>
									<div className={style.name}>{dynamicGroup.group.name}</div>
									{dynamicGroupConfigDescription(dynamicGroup.group.consumerCatalogConfiguration)}
								</div>
								{allowedToEdit ? (
									<FontAwesomeIcon
										icon={faAngleRight}
										size={"xl"}
										style={{ alignSelf: "center", marginLeft: "auto" }}
									/>
								) : null}
							</div>
						)
					})}
					{fixedGroups ? (
						<>
							<div
								className={style.consumerGroup}
								onClick={() => {
									if (allowedToEdit) {
										setEditGroup({
											type: "Fixed",
											group: {
												type: "Company",
												fixedGroup: fixedGroups.Companies,
											},
										})
									}
								}}>
								<div className={style.consumerGroupContent}>
									<div className={style.name}>{fixedGroups.Companies.name}</div>
									{dynamicGroupConfigDescription(fixedGroups.Companies.consumerCatalogConfiguration)}
								</div>
								{allowedToEdit ? (
									<FontAwesomeIcon
										icon={faAngleRight}
										size={"xl"}
										style={{ alignSelf: "center", marginLeft: "auto" }}
									/>
								) : null}
							</div>
							<div
								className={style.consumerGroup}
								onClick={() => {
									if (allowedToEdit) {
										setEditGroup({
											type: "Fixed",
											group: {
												type: "PrivatePerson",
												fixedGroup: fixedGroups.PrivatePersons,
											},
										})
									}
								}}>
								<div className={style.consumerGroupContent}>
									<div className={style.name}>{fixedGroups.PrivatePersons.name}</div>
									{dynamicGroupConfigDescription(fixedGroups.PrivatePersons.consumerCatalogConfiguration)}
								</div>
								{allowedToEdit ? (
									<FontAwesomeIcon
										icon={faAngleRight}
										size={"xl"}
										style={{ alignSelf: "center", marginLeft: "auto" }}
									/>
								) : null}
							</div>
						</>
					) : null}
				</div>
			</div>
		)
	}

	function dynamicGroupConfigDescription(consumerCatalogConfiguration: ConsumerCatalogConfiguration) {
		return exhaustive(consumerCatalogConfiguration, "type", {
			Contract: () => {
				return (
					<>
						<div>Typ: Befintligt kontrakt</div>
						<div>
							Priser visas: <strong>Inga priser</strong>
						</div>
					</>
				)
			},
			Postpay: (it) => {
				return (
					<>
						<div>Typ: Förskottsbetalning</div>
						<div>
							Priser visas: <strong>{vatPolicyToHumanBooleanText(it.preferredVATRenderPolicy)}</strong>
						</div>
					</>
				)
			},
			Prepay: (it) => {
				return (
					<>
						<div>Typ: Efterskottsbetalning</div>
						<div>
							Priser visas: <strong>{vatPolicyToHumanBooleanText(it.preferredVATRenderPolicy)}</strong>
						</div>
					</>
				)
			},
		})
	}

	if (!permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Groups_Read)) {
		return null
	}

	return (
		<>
			{assignConsumersModal()}
			<div className={style.wrapper}>
				{dataLoading ? (
					<div style={{ display: "flex", justifyContent: "center" }}>
						<Loader />
					</div>
				) : (
					dynamicGroupsSection()
				)}
			</div>
		</>
	)
}

export function vatPolicyToHumanBooleanText(preferredVATRenderPolicy: PreferredVATRenderPolicy) {
	return exhaustive(preferredVATRenderPolicy, {
		[PreferredVATRenderPolicy.IncludeVAT]: () => "Inkl. moms",
		[PreferredVATRenderPolicy.IncludeVATExplicit]: () => "Inkl. moms",
		[PreferredVATRenderPolicy.ExcludeVAT]: () => "Exkl. moms",
	})
}
